import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {Box, InputBase, InputBaseProps, MenuItem, Select} from "@mui/material";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {TaxRate} from "crqlar-react-common/dist/models/rate/taxRate.model";
import {forwardRef} from "react";
import {useTranslation} from "react-i18next";
import {FolioCellBox, FolioCellText} from "./FolioCellBox";
import {FolioFormat} from "./folioFormat";
import {hasVat} from "./folioLine.util";
import {vatPatch} from "./folioVat.util";

const NONE = '';
// The line's own VAT that isn't in the list: a 0 % stamp, or a rate the list doesn't (yet) have
const OWN = '__own__';

// The cell box draws the outline, so the select sits in a bare InputBase. Select hands its input a
// `notched` flag (meant for OutlinedInput) with displayEmpty - it must not reach the DOM.
const BareInput = forwardRef<HTMLDivElement, InputBaseProps & {notched?: boolean}>(({notched, ...props}, ref) => (
    <InputBase ref={ref} {...props}/>
));
BareInput.displayName = 'BareInput';

interface Props {
    line: FolioItem
    taxRates: TaxRate[]
    format: FolioFormat
    /** Flag a missing VAT: Casablanca needs one on every line. */
    warnMissing: boolean
    onChange: (patch: Partial<FolioItem>) => void
    onBlur?: () => void
    ariaLabel: string
}

/** The line's VAT as "20 % incl."; the list names each rate. A missing one is a warning in the box. */
const FolioVatSelect = ({line, taxRates, format, warnMissing, onChange, onBlur, ariaLabel}: Props) => {
    const {t} = useTranslation();
    const listed = taxRates.find(taxRate => !!line.taxRateId && taxRate.id === line.taxRateId);
    const own = !listed && hasVat(line);
    const value = listed?.id ?? (own ? OWN : NONE);

    const renderValue = () => {
        if (listed) return <FolioCellText value={format.vat(listed) ?? '…'}/>;
        if (own) return <FolioCellText value={format.vat(line.taxRateSnap) ?? '…'}/>;
        if (!warnMissing) return <FolioCellText value={'—'} muted/>;
        return (
            <Box component={'span'} sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, color: 'warning.dark'}}>
                <WarningAmberIcon sx={{fontSize: 14, flexShrink: 0}}/>
                <FolioCellText value={t('No VAT')}/>
            </Box>
        );
    };

    return (
        <FolioCellBox editable tone={warnMissing ? 'warning' : 'default'} align={'right'} sx={{px: 0}}>
            <Select
                fullWidth
                displayEmpty
                value={value}
                renderValue={renderValue}
                onChange={event => {
                    const next = event.target.value;
                    if (next === OWN) return;
                    onChange(vatPatch(line, taxRates.find(taxRate => taxRate.id === next)));
                }}
                onClose={onBlur}
                input={<BareInput/>}
                inputProps={{'aria-label': ariaLabel}}
                sx={{
                    height: '100%',
                    fontSize: 'inherit',
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        // Right-aligned like every value; the arrow keeps its place after it
                        justifyContent: 'flex-end',
                        height: '100%',
                        minHeight: 0,
                        boxSizing: 'border-box',
                        py: 0,
                        pl: 1,
                        pr: '22px !important',
                    },
                    '& .MuiSelect-icon': {right: 2, fontSize: 18},
                }}
                MenuProps={{MenuListProps: {dense: true}}}
            >
                <MenuItem value={NONE}>{t('No VAT')}</MenuItem>
                {own && <MenuItem value={OWN}>{format.vat(line.taxRateSnap) ?? '…'}</MenuItem>}
                {taxRates.map(taxRate => (
                    <MenuItem key={taxRate.id} value={taxRate.id}>
                        {[taxRate.name, format.vat(taxRate)].filter(Boolean).join(' · ')}
                    </MenuItem>
                ))}
            </Select>
        </FolioCellBox>
    );
};

export default FolioVatSelect;
